/**
 * TimelinePreviewPlayer
 *
 * Shared multi-track preview wrapper used by:
 *  - Canvas preview (VideoEditorNode) — readOnly, with custom chrome overlays
 *  - (future) Anywhere we want to embed the same playback engine outside the
 *    Timeline Editor modal.
 *
 * Owns:
 *  - Conversion of edge-resolved video/audio refs → TimelineClip[] /
 *    TimelineAudioClip[]
 *  - useClipPreloader (blob URLs)
 *  - blob-based duration probing
 *  - useTimelineState (timeline draft)
 *  - useTimelineYjsSync (restore the modal-saved draft; readOnly here)
 *  - useTimelinePlayback (RAF playhead loop)
 *  - useAudioPool (multi-track audio, with globalMuted)
 *  - <VideoPreviewPlayer> (premounted <video> per clip; zero-gap transitions)
 *
 * Exposes a renderOverlays(controls) render-prop so the host can inject its
 * own clip badge / mute / fullscreen / bottom bar.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { VideoPreviewPlayer } from './VideoPreviewPlayer'
import { useTimelineState } from './useTimelineState'
import { useTimelinePlayback } from './useTimelinePlayback'
import { useTimelineYjsSync } from './useTimelineYjsSync'
import { useClipPreloader } from './useClipPreloader'
import { useAudioPool } from './useAudioPool'
import { computeTimelineDuration } from './timelineUtils'
import { useYDoc } from '../../../../hooks/useYDoc'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import type { TimelineAudioClip, TimelineClip } from './types'

export interface TimelinePreviewClipRef {
  id: string
  path: string
  name: string
  /** Upstream-known duration in seconds (e.g. video_generator.out_video_duration).
   * Used to initialize the timeline correctly instead of a 5s placeholder. */
  duration?: number
}

export interface TimelinePreviewAudioRef extends TimelinePreviewClipRef {
  role?: 'original' | 'replacement' | 'sfx'
}

export interface TimelinePreviewControls {
  isPlaying: boolean
  togglePlayPause: () => void
  playheadPosition: number
  totalDuration: number
  setPlayhead: (position: number) => void
  currentClipIndex: number | null
  currentClipName: string | null
  videoCount: number
  audioCount: number
  isMuted: boolean
  toggleMute: () => void
  /** Ref pointing at the wrapper div (used by host for native fullscreen). */
  previewElementRef: React.RefObject<HTMLDivElement>
}

interface TimelinePreviewPlayerProps {
  nodeId: string
  videoClips: TimelinePreviewClipRef[]
  audioClips: TimelinePreviewAudioRef[]
  /** When true, never write to the Yjs timeline draft. */
  readOnly?: boolean
  /** When true, hide the built-in top-left clip badge (host renders its own). */
  hideClipBadge?: boolean
  /** Render-prop for chrome overlays (clip badge, mute, fullscreen, bottom bar). */
  renderOverlays?: (controls: TimelinePreviewControls) => ReactNode
}

export function TimelinePreviewPlayer({
  nodeId,
  videoClips,
  audioClips,
  readOnly = false,
  hideClipBadge = false,
  renderOverlays,
}: TimelinePreviewPlayerProps) {
  const projectId = useProjectId()
  const [isMuted, setIsMuted] = useState(false)
  const previewElementRef = useRef<HTMLDivElement>(null)

  // ── Convert refs into TimelineClip / TimelineAudioClip ──────────────
  // Same duration source-of-truth ladder as TimelineEditorModal:
  //   1. clip.duration from upstream video_generator's out_video_duration
  //   2. 5s placeholder for legacy / upload nodes (replaced by async probe)
  const initialClips: TimelineClip[] = useMemo(
    () =>
      videoClips.map((clip, index) => {
        const duration = clip.duration ?? 5
        const startOffset = videoClips
          .slice(0, index)
          .reduce((acc, prev) => acc + (prev.duration ?? 5), 0)
        return {
          id: clip.id,
          sourceNodeId: clip.id,
          sourcePath: clip.path,
          originalDuration: duration,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: duration,
          order: index,
          startOffset,
          trackIndex: 0,
          label: clip.name,
          speed: 1.0,
          volume: 1.0,
          muted: false,
        }
      }),
    [videoClips],
  )

  const initialAudioClips: TimelineAudioClip[] = useMemo(
    () =>
      audioClips.map((af) => ({
        id: af.id,
        sourceNodeId: af.id,
        sourcePath: af.path,
        originalDuration: 15,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: 15,
        volume: 0.8,
        muted: false,
        startOffset: 0,
        role: af.role || 'original',
        speed: 1.0,
        trackIndex: 0,
      })),
    [audioClips],
  )

  const videoClipsKey = videoClips.map((c) => `${c.id}:${c.path}`).join('|')
  const audioFileKey = audioClips.map((a) => `${a.id}:${a.path}`).join('|')

  const {
    timelineState,
    setPlayhead,
    togglePlayPause,
    restoreState,
  } = useTimelineState()

  const timelineStateRef = useRef(timelineState)
  timelineStateRef.current = timelineState

  // ── Preload all source paths as blobs ─────────────────────────────
  const allSourcePaths = useMemo(() => {
    const set = new Set<string>()
    for (const c of videoClips) set.add(c.path)
    for (const a of audioClips) set.add(a.path)
    return Array.from(set)
  }, [videoClips, audioClips])

  const preloader = useClipPreloader({ projectId, sourcePaths: allSourcePaths })

  // ── Audio pool (with global mute override) ────────────────────────
  useAudioPool({
    audioClips: timelineState.audioClips,
    playheadPosition: timelineState.playheadPosition,
    isPlaying: timelineState.isPlaying,
    preloader,
    globalMuted: isMuted,
  })

  // ── Probe real durations from blob URLs ────────────────────────────
  const loadedDurationsRef = useRef<Map<string, number>>(new Map())
  const [durationsLoaded, setDurationsLoaded] = useState(false)
  const prevKeysRef = useRef(`${videoClipsKey}|${audioFileKey}`)

  useEffect(() => {
    const currentKeys = `${videoClipsKey}|${audioFileKey}`
    if (prevKeysRef.current !== currentKeys) {
      prevKeysRef.current = currentKeys
      loadedDurationsRef.current = new Map()
      setDurationsLoaded(false)
    }
  }, [videoClipsKey, audioFileKey])

  useEffect(() => {
    if ((videoClips.length === 0 && audioClips.length === 0) || durationsLoaded) return
    const allPaths = [...videoClips.map((c) => c.path), ...audioClips.map((a) => a.path)]
    // A path is "resolved" when EITHER its blob has been loaded OR it
    // has been added to the preloader's errorPaths set (retries
    // exhausted).  Previously we waited for ``allReady`` (every blob
    // present), which meant a single broken/missing source — e.g. a
    // 0-byte audio file from a failed BGM generation — would stall the
    // duration probe forever and brick the entire timeline UI.
    const errorPathSet = new Set(preloader.errorPaths)
    const allResolved = allPaths.every(
      (p) => preloader.getBlobUrl(p) !== null || errorPathSet.has(p),
    )
    if (!allResolved) return

    let cancelled = false
    const run = async () => {
      const durations = new Map<string, number>()
      const videoPromises = videoClips.map(async (clip) => {
        const blobUrl = preloader.getBlobUrl(clip.path)
        // Missing blob -> clip stays at the placeholder originalDuration
        // (5s) declared in initialClips. The player UI will surface it
        // as unavailable via the errorPaths set; we don't block the rest
        // of the timeline waiting for it.
        if (!blobUrl) return
        const d = await new Promise<number>((resolve, reject) => {
          const v = document.createElement('video')
          v.preload = 'metadata'
          v.onloadedmetadata = () => resolve(v.duration)
          v.onerror = () => reject(new Error('video metadata failed'))
          v.src = blobUrl
        }).catch(() => null)
        if (d && Number.isFinite(d) && d > 0) durations.set(clip.id, d)
      })
      const audioPromises = audioClips.map(async (af) => {
        const blobUrl = preloader.getBlobUrl(af.path)
        if (!blobUrl) return
        const d = await new Promise<number>((resolve, reject) => {
          const a = new Audio()
          a.preload = 'metadata'
          a.onloadedmetadata = () => resolve(a.duration)
          a.onerror = () => reject(new Error('audio metadata failed'))
          a.src = blobUrl
        }).catch(() => null)
        if (d && Number.isFinite(d) && d > 0) durations.set(af.id, d)
      })
      await Promise.all([...videoPromises, ...audioPromises])
      if (!cancelled) {
        loadedDurationsRef.current = durations
        setDurationsLoaded(true)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [durationsLoaded, preloader, videoClips, audioClips])

  // Apply real durations once both video and audio are loaded.
  const clipDurationFingerprint = timelineState.clips
    .map((c) => `${c.id}:${c.originalDuration}`)
    .join(',')

  useEffect(() => {
    if (!durationsLoaded || timelineState.clips.length === 0) return
    const durations = loadedDurationsRef.current
    if (durations.size === 0) return

    const current = timelineStateRef.current
    const videoNeeds = current.clips.some((c) => {
      const real = durations.get(c.sourceNodeId) ?? durations.get(c.id)
      return real && real !== c.originalDuration
    })
    const audioNeeds = current.audioClips.some((c) => {
      const real = durations.get(c.sourceNodeId) ?? durations.get(c.id)
      return real && real !== c.originalDuration
    })
    if (!videoNeeds && !audioNeeds) return

    const newClips = current.clips.map((c) => {
      const real = durations.get(c.sourceNodeId) ?? durations.get(c.id)
      if (real && real !== c.originalDuration) {
        return {
          ...c,
          originalDuration: real,
          effectiveDuration: real - c.trimStart - c.trimEnd,
        }
      }
      return c
    })
    const newAudioClips = current.audioClips.map((c) => {
      const real = durations.get(c.sourceNodeId) ?? durations.get(c.id)
      if (real && real !== c.originalDuration) {
        return {
          ...c,
          originalDuration: real,
          effectiveDuration: real - c.trimStart - c.trimEnd,
        }
      }
      return c
    })
    restoreState({
      ...current,
      clips: newClips,
      audioClips: newAudioClips,
      totalDuration: computeTimelineDuration(newClips, newAudioClips),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationsLoaded, clipDurationFingerprint, restoreState])

  // ── Yjs restore (read-only on canvas) ─────────────────────────────
  const { ydoc, synced } = useYDoc({
    projectId: projectId || '',
    canvasId: 'agent',
    enabled: !!projectId,
  })
  const validVideoSourceIds = useMemo(
    () => new Set(videoClips.map((c) => c.id)),
    [videoClips],
  )
  const validAudioSourceIds = useMemo(
    () => new Set(audioClips.map((c) => c.id)),
    [audioClips],
  )

  useTimelineYjsSync({
    ydoc,
    synced,
    timelineState,
    nodeId,
    onRestoreState: restoreState,
    validVideoSourceIds,
    validAudioSourceIds,
    readOnly,
  })

  // ── Initialise clips on mount, merge edge updates ────────────────
  const mergeAttemptedRef = useRef(false)
  useEffect(() => {
    if (initialClips.length === 0) return
    if (timelineState.clips.length === 0) {
      mergeAttemptedRef.current = false
      const totalDuration = computeTimelineDuration(initialClips, initialAudioClips)
      restoreState({
        clips: initialClips,
        audioClips: initialAudioClips,
        playheadPosition: 0,
        zoomLevel: 50,
        isPlaying: false,
        selectedClipIds: new Set<string>(),
        totalDuration,
        videoTrackCount: 1,
        audioTrackCount: 1,
        copiedClip: null,
        copiedClipType: null,
        // RFC-0651
        mutedVideoTracks: new Set<number>(),
        mutedAudioTracks: new Set<number>(),
        magnetEnabled: true,
      })
    } else if (!mergeAttemptedRef.current) {
      mergeAttemptedRef.current = true
      const current = timelineStateRef.current
      const initialIds = new Set(initialClips.map((c) => c.sourceNodeId))
      const existingIds = new Set(current.clips.map((c) => c.sourceNodeId))
      const newClips = initialClips.filter((c) => !existingIds.has(c.sourceNodeId))
      const prunedClips = current.clips.filter((c) => initialIds.has(c.sourceNodeId))
      const initialAudioIds = new Set(initialAudioClips.map((c) => c.sourceNodeId))
      const existingAudioIds = new Set(current.audioClips.map((c) => c.sourceNodeId))
      const newAudioClips = initialAudioClips.filter((c) => !existingAudioIds.has(c.sourceNodeId))
      const prunedAudioClips = current.audioClips.filter((c) => initialAudioIds.has(c.sourceNodeId))

      if (
        newClips.length > 0 ||
        newAudioClips.length > 0 ||
        prunedClips.length !== current.clips.length ||
        prunedAudioClips.length !== current.audioClips.length
      ) {
        const maxOrder = prunedClips.reduce((m, c) => Math.max(m, c.order), -1)
        const nextStart = computeTimelineDuration(prunedClips, current.audioClips)
        const merged = [
          ...prunedClips,
          ...newClips.map((c, i) => ({
            ...c,
            order: maxOrder + 1 + i,
            startOffset: nextStart + i * c.effectiveDuration,
          })),
        ]
        const mergedAudio = [...prunedAudioClips, ...newAudioClips]
        restoreState({
          ...current,
          clips: merged,
          audioClips: mergedAudio,
          totalDuration: computeTimelineDuration(merged, mergedAudio),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialClips, initialAudioClips, timelineState.clips.length, restoreState])

  // ── Playback hook ────────────────────────────────────────────────
  const { togglePlayPause: handleTogglePlayPause } = useTimelinePlayback({
    timelineState,
    setPlayhead,
    togglePlayPause,
  })

  const toggleMute = useCallback(() => setIsMuted((m) => !m), [])

  // ── Active clip introspection for overlays ───────────────────────
  const activeClipIndex = useMemo(() => {
    const sorted = [...timelineState.clips].sort(
      (a, b) =>
        a.trackIndex - b.trackIndex ||
        a.startOffset - b.startOffset ||
        a.order - b.order,
    )
    const idx = sorted.findIndex(
      (c) =>
        timelineState.playheadPosition >= c.startOffset &&
        timelineState.playheadPosition < c.startOffset + c.effectiveDuration,
    )
    return idx === -1 ? null : idx
  }, [timelineState.clips, timelineState.playheadPosition])

  const activeClipName = useMemo(() => {
    if (activeClipIndex == null) return null
    const sorted = [...timelineState.clips].sort(
      (a, b) =>
        a.trackIndex - b.trackIndex ||
        a.startOffset - b.startOffset ||
        a.order - b.order,
    )
    return sorted[activeClipIndex]?.label ?? null
  }, [timelineState.clips, activeClipIndex])

  const controls: TimelinePreviewControls = {
    isPlaying: timelineState.isPlaying,
    togglePlayPause: handleTogglePlayPause,
    playheadPosition: timelineState.playheadPosition,
    totalDuration: timelineState.totalDuration,
    setPlayhead,
    currentClipIndex: activeClipIndex,
    currentClipName: activeClipName,
    videoCount: timelineState.clips.length,
    audioCount: timelineState.audioClips.length,
    isMuted,
    toggleMute,
    previewElementRef,
  }

  return (
    <div
      ref={previewElementRef}
      className="relative w-full h-full"
      data-testid="timeline-preview-player"
    >
      <VideoPreviewPlayer
        clips={timelineState.clips}
        playheadPosition={timelineState.playheadPosition}
        isPlaying={timelineState.isPlaying}
        onVideoEnd={() => togglePlayPause()}
        preloader={preloader}
        globalMuted={isMuted}
        hideClipBadge={hideClipBadge}
      />
      {renderOverlays && renderOverlays(controls)}
    </div>
  )
}
